import { useState } from "react";
import { Pencil, Trash2, Eye, EyeOff, ExternalLink, Copy, Check, Archive, CalendarPlus, Luggage } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useStepUp } from "../StepUpGuard";
import PhotoAttach from "../PhotoAttach";
import { KIND_CONFIG, kindTitle, COVERAGE_OPTIONS } from "./walletConfig";
import { StatusBadge, fmt } from "./WalletShared";

const MASKED = { card: ["rewards_number"], certificate: ["number"], credit: ["number"], pool: ["admin_number"] };

/* Generic display card with actions: edit, apply-to-trip, mark used (partial for credits/certs),
   copy code, reveal masked numbers, uploads, archive, delete. */
export function WalletItemCard({ kind, it, trips, onEdit, onChange }) {
  const cfg = KIND_CONFIG[kind];
  const [rev, setRev] = useState(null);
  const [applying, setApplying] = useState(false);
  const [using, setUsing] = useState(false);
  const [amount, setAmount] = useState("");
  const [copied, setCopied] = useState(false);
  const guard = useStepUp();

  const maskedKeys = MASKED[kind] || [];
  const hasSecret = maskedKeys.some((k) => it[`has_${k}`]);

  const reveal = () => {
    if (rev) { setRev(null); return; }
    guard(async () => {
      try { const r = await api.get(`/wallet/items/${kind}/${it.id}/reveal`); setRev(r.data); }
      catch { toast.error("Could not reveal — please try again"); }
    });
  };

  const applyToTrip = async (tid) => {
    try {
      await api.post(`/wallet/items/${kind}/${it.id}/apply`, { trip_id: tid });
      toast.success("Attached to trip — it is NOT marked used until you confirm redemption");
      setApplying(false); onChange();
    } catch { toast.error("Could not attach to trip"); }
  };

  const markUsed = async (amt) => {
    try {
      const r = await api.post(`/wallet/items/${kind}/${it.id}/use`, amt ? { amount: amt } : {});
      toast.success(r.data.status === "used" ? "Marked as used" : amt ? `Logged — ${fmt(r.data.remaining_value)} remaining` : "Updated");
      setUsing(false); setAmount(""); onChange();
    } catch { toast.error("Could not update"); }
  };

  const archive = async () => { await api.post(`/wallet/items/${kind}/${it.id}/archive`); onChange(); };
  const remove = async () => { await api.delete(`/wallet/items/${kind}/${it.id}`); toast.success("Removed"); onChange(); };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(it.code || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };

  const covLabel = COVERAGE_OPTIONS.find(([v]) => v === it.coverage)?.[1];
  const val = (k) => rev?.[k] ?? it[k];
  const partial = kind === "credit" || kind === "certificate";
  const remaining = it.remaining_value ?? null;

  return (
    <div className="border border-zinc-300 bg-white p-4 flex flex-col" data-testid={`wallet-item-${kind}-${it.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-900 truncate">{kindTitle(kind, it)}</div>
          <div className="text-xs text-muted-foreground truncate">{[it.airline, it.cert_type !== kindTitle(kind, it) ? it.cert_type : null, it.issuer, it.program].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} data-testid={`wallet-edit-${it.id}`} className="text-muted-foreground hover:text-primary p-1"><Pencil className="w-4 h-4" /></button>
          <button onClick={archive} title={it.status === "archived" ? "Unarchive" : "Archive"} data-testid={`wallet-archive-${it.id}`} className="text-muted-foreground hover:text-amber-600 p-1"><Archive className="w-4 h-4" /></button>
          <button onClick={remove} data-testid={`wallet-remove-${it.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <StatusBadge it={it} />
        {it.applied_trips?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-700 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700" data-testid={`wallet-applied-${it.id}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> On trip: {it.applied_trips[0].trip_title}
          </span>
        )}
      </div>

      <div className="mt-2 space-y-1 text-sm">
        {kind === "code" && it.code && (
          <div className="flex items-center gap-2">
            <span className="font-mono font-700 bg-orange-50 border border-orange-200 px-2 py-0.5" data-testid={`wallet-code-value-${it.id}`}>{it.code}</span>
            <button onClick={copyCode} data-testid={`wallet-copy-code-${it.id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copy code
            </button>
          </div>
        )}
        {kind === "pool" && (
          <div><span className="text-muted-foreground">Available: </span><b className="tabular-nums">{fmt(it.available_miles)} {it.unit || "miles"}</b>
            {it.pending_miles ? <span className="text-xs text-muted-foreground"> · {fmt(it.pending_miles)} pending</span> : null}</div>
        )}
        {(it.value || it.original_value) && (
          <div><span className="text-muted-foreground">Value: </span>
            <b>{it.currency ? `${it.currency} ` : "$"}{fmt(it.original_value || it.value)}</b>
            {remaining != null && Number(remaining) !== Number(it.original_value || it.value) &&
              <span className="text-emerald-700 font-600"> · {it.currency ? `${it.currency} ` : "$"}{fmt(remaining)} remaining</span>}
          </div>
        )}
        {it.visits_remaining != null && it.visits_remaining !== "" && <div><span className="text-muted-foreground">Visits left: </span><b>{fmt(it.visits_remaining)}</b>{it.guest_allowance ? ` · ${it.guest_allowance}` : ""}</div>}
        {it.last4 && <div className="font-mono text-xs text-muted-foreground">Card •••• {it.last4}{it.annual_fee ? ` · fee ${it.annual_fee}` : ""}</div>}
        {maskedKeys.map((k) => it[`has_${k}`] && (
          <div key={k} className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">No: </span>
            <span className="font-mono" data-testid={`wallet-masked-${k}-${it.id}`}>{val(k)}</span>
            <button onClick={reveal} data-testid={`wallet-reveal-${it.id}`} className="text-primary">{rev ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
          </div>
        ))}
        {Array.isArray(it.benefits) && it.benefits.length > 0 && (
          <ul className="text-xs text-zinc-700 list-disc ml-4">{it.benefits.slice(0, 6).map((b, i) => <li key={i}>{b}</li>)}</ul>
        )}
        {covLabel && <div className="text-xs text-muted-foreground flex items-center gap-1"><Luggage className="w-3 h-3" /> Covers: {covLabel}</div>}
        {(it.eligible_routes || it.restrictions) && <div className="text-xs text-muted-foreground truncate" title={`${it.eligible_routes || ""} ${it.restrictions || ""}`}>{it.eligible_routes || it.restrictions}</div>}
        {it.notes && <div className="text-xs text-muted-foreground">{it.notes}</div>}
      </div>

      {applying && (
        <div className="mt-2 border border-blue-200 bg-blue-50/50 p-2" data-testid={`wallet-apply-picker-${it.id}`}>
          <div className="text-xs font-700 mb-1">Attach to which trip?</div>
          {trips?.length ? trips.map((t) => (
            <button key={t.id} onClick={() => applyToTrip(t.id)} className="block w-full text-left text-xs px-2 py-1 hover:bg-white" data-testid={`wallet-apply-trip-${t.id}`}>
              {t.title} {t.start_date ? `· ${t.start_date}` : ""}
            </button>
          )) : <div className="text-xs text-muted-foreground">No trips yet — create one in My Trips first.</div>}
        </div>
      )}

      {using && partial && (
        <div className="mt-2 flex gap-2" data-testid={`wallet-partial-${it.id}`}>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount used (blank = all)"
            className="flex-1 border border-zinc-300 px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary" />
          <button onClick={() => markUsed(amount || null)} data-testid={`wallet-use-confirm-${it.id}`} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-600">Confirm</button>
        </div>
      )}

      <div className="mt-3">
        <PhotoAttach items={it.photos || []} testPrefix={`wallet-photo-${it.id}`} label="Add a screenshot / image"
          onUpload={async (file) => { const fd = new FormData(); fd.append("file", file); await api.post(`/wallet/items/${kind}/${it.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }); onChange(); }}
          onDelete={async (pid) => { await api.delete(`/wallet/items/${kind}/${it.id}/photos/${pid}`); onChange(); }} />
      </div>

      <div className="mt-auto pt-3 flex flex-wrap gap-1.5">
        {it.effective_status === "active" && (
          <>
            <button onClick={() => setApplying((a) => !a)} data-testid={`wallet-apply-${it.id}`} className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-1.5 text-xs hover:border-blue-500 text-blue-700"><CalendarPlus className="w-3.5 h-3.5" /> Apply to trip</button>
            <button onClick={() => (partial ? setUsing((u) => !u) : markUsed(null))} data-testid={`wallet-use-${it.id}`} className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-1.5 text-xs hover:border-emerald-500 text-emerald-700"><Check className="w-3.5 h-3.5" /> Mark used</button>
          </>
        )}
        {it.effective_status === "used" && (
          <button onClick={() => api.post(`/wallet/items/${kind}/${it.id}/use`, { undo: true }).then(onChange)} data-testid={`wallet-unuse-${it.id}`} className="border border-zinc-300 px-2 py-1.5 text-xs">Undo used</button>
        )}
        {(it.url || it.rules_url) && (
          <a href={it.url || it.rules_url} target="_blank" rel="noreferrer" data-testid={`wallet-url-${it.id}`} className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-1.5 text-xs hover:border-primary text-primary"><ExternalLink className="w-3.5 h-3.5" /> Open site</a>
        )}
      </div>
    </div>
  );
}
